import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, BuildingIcon, UsersIcon, FileTextIcon } from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Radio } from '../components/ui/Radio';
type StakeholderRole = 'admin' | 'regulator';
const roleOptions = [{
  value: 'admin' as const,
  label: 'AeroZeep Admin',
  description: 'Manage refunds, invoices, and operations',
  icon: BuildingIcon
}, {
  value: 'regulator' as const,
  label: 'NCAA Regulator',
  description: 'Monitor compliance and audit trails',
  icon: FileTextIcon
}];
export function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<StakeholderRole>('admin');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Mock authentication
    if (email && password) {
      if (role === 'admin') {
        navigate(ROUTES.ADMIN);
      } else {
        navigate(ROUTES.REGULATOR);
      }
    } else {
      setError('Please enter your email and password');
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-surface-900 via-surface-800 to-surface-900 relative overflow-hidden" data-id="element-917">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-10" data-id="element-918">
        <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} data-id="element-919" />
      </div>

      {/* Glowing Orbs */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-primary-500 rounded-full blur-3xl opacity-20 animate-float" data-id="element-920" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent-500 rounded-full blur-3xl opacity-20 animate-float" style={{
      animationDelay: '2s'
    }} data-id="element-921" />

      <div className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12" data-id="element-922">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-lg" data-id="element-923">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex items-center justify-center gap-3 mb-8" data-id="element-924">
            <motion.div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30" whileHover={{
            scale: 1.05,
            rotate: -5
          }} data-id="element-925">
              <ShieldCheckIcon className="w-7 h-7 text-white" data-id="element-926" />
            </motion.div>
            <div className="text-left" data-id="element-927">
              <span className="block text-display-sm font-bold text-white" data-id="element-928">
                AeroZeep
              </span>
              <span className="block text-caption text-surface-400" data-id="element-929">
                Stakeholder Portal
              </span>
            </div>
          </Link>

          {/* Login Card */}
          <Card variant="elevated" padding="lg" className="shadow-2xl border-0 bg-surface-800/50 backdrop-blur-xl" data-id="element-930">
            <div className="text-center mb-8" data-id="element-931">
              <h1 className="text-display-sm font-bold text-white mb-2" data-id="element-932">
                Stakeholder Access
              </h1>
              <p className="text-body-md text-surface-300" data-id="element-933">
                Secure login for authorized personnel
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6" data-id="element-934">
              {/* Role Selection */}
              <div className="space-y-3" data-id="element-935">
                <label className="block text-body-sm font-medium text-surface-200 mb-3" data-id="element-936">
                  Select Your Role
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-937">
                  {roleOptions.map(option => {
                  const Icon = option.icon;
                  return <motion.button key={option.value} type="button" onClick={() => setRole(option.value)} className={`p-4 rounded-xl border-2 transition-all text-left ${role === option.value ? 'border-primary-500 bg-primary-500/10' : 'border-surface-600 bg-surface-700/30 hover:border-surface-500'}`} whileHover={{
                    scale: 1.02
                  }} whileTap={{
                    scale: 0.98
                  }} data-id="element-938">
                        <Icon className={`w-6 h-6 mb-2 ${role === option.value ? 'text-primary-400' : 'text-surface-400'}`} data-id="element-939" />
                        <div className="text-body-sm font-semibold text-white mb-1" data-id="element-940">
                          {option.label}
                        </div>
                        <div className="text-caption text-surface-400" data-id="element-941">
                          {option.description}
                        </div>
                      </motion.button>;
                })}
                </div>
              </div>

              <Input label="Email Address" type="email" placeholder="your.email@domain.com" value={email} onChange={e => setEmail(e.target.value)} leftIcon={<MailIcon className="w-4 h-4" data-id="element-943" />} required className="bg-surface-700/50 border-surface-600 text-white placeholder:text-surface-400" data-id="element-942" />

              <div className="relative" data-id="element-944">
                <Input label="Password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} leftIcon={<LockIcon className="w-4 h-4" data-id="element-946" />} required className="bg-surface-700/50 border-surface-600 text-white placeholder:text-surface-400" data-id="element-945" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[38px] text-surface-400 hover:text-surface-200 transition-colors" data-id="element-947">
                  {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-948" /> : <EyeIcon className="w-4 h-4" data-id="element-949" />}
                </button>
              </div>

              {error && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-3 bg-error-500/10 border border-error-500/30 rounded-lg" data-id="element-950">
                  <p className="text-body-sm text-error-300" data-id="element-951">{error}</p>
                </motion.div>}

              <Button type="submit" fullWidth loading={isLoading} icon={<ArrowRightIcon className="w-4 h-4" data-id="element-953" />} iconPosition="right" className="shadow-lg shadow-primary-500/20 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700" data-id="element-952">
                Sign In Securely
              </Button>
            </form>

            {/* Demo Credentials */}
            <div className="mt-6 p-4 bg-surface-700/30 rounded-xl border border-surface-600" data-id="element-954">
              <p className="text-body-sm font-medium text-surface-200 mb-2" data-id="element-955">
                Demo Credentials:
              </p>
              <div className="space-y-1 text-caption text-surface-400" data-id="element-956">
                <p data-id="element-957">• Admin: admin@aerozeep.com + any password</p>
                <p data-id="element-958">• Regulator: ncaa@regulator.gov.ng + any password</p>
              </div>
            </div>

            {/* Customer Login Link */}
            <div className="mt-6 text-center" data-id="element-959">
              <p className="text-body-sm text-surface-400" data-id="element-960">
                Looking for customer login?{' '}
                <Link to={ROUTES.LOGIN} className="font-semibold text-primary-400 hover:text-primary-300 transition-colors" data-id="element-961">
                  Go to customer portal
                </Link>
              </p>
            </div>
          </Card>

          {/* Security Badges */}
          <div className="flex items-center justify-center gap-6 mt-8 text-surface-400" data-id="element-962">
            <div className="flex items-center gap-2" data-id="element-963">
              <ShieldCheckIcon className="w-4 h-4 text-success-400" data-id="element-964" />
              <span className="text-body-sm" data-id="element-965">256-bit Encryption</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-966">
              <UsersIcon className="w-4 h-4 text-primary-400" data-id="element-967" />
              <span className="text-body-sm" data-id="element-968">2FA Protected</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}